import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { ENV_BUILD, environment } from 'src/environments/environment';
import { StemexeDataProviderService } from './data-providers/services/stemexe-data-provider.service';
import { GroupsService } from './groups/groups.service';
import { ExdrLanguageService } from './languages/services/exdr-language.service';
import { AuthService } from './shared/services/auth.service';
import { MetaService } from './shared/services/meta.service';
import { SharedService } from './shared/shared.service';
import { ConsoleToggleService } from '@plannexe/shared/services/console-toggle-service.service';
import { BreadcrumbService } from 'src/app/requests/esp/service/breadcrumb/breadcrumb.service';
declare let gtag: (...args) => {};
@Component({
  selector: 'xcdrs-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  public showSpinner: boolean;
  timer: any;
  private previousUrl: string;
  private currentUrl: string;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _translate: TranslateService,
    public exdrLanguageService: ExdrLanguageService,
    private sharedService: SharedService,
    public authService: AuthService,
    private _router: Router,
    private _route: ActivatedRoute,
    private metaService: MetaService,
    private groupsService: GroupsService,
    private stemexeDataProvider: StemexeDataProviderService,
    private consoleToggleService: ConsoleToggleService,
    private breadcrumbService: BreadcrumbService,
    @Inject(PLATFORM_ID) public platformId) {
    _translate.setDefaultLang('en');

    this._router.events.subscribe((routerEvent: Event) => {
      if (routerEvent instanceof NavigationStart) {
        this.showSpinner = true;
      }

      if (routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationError || routerEvent instanceof NavigationCancel) {
        this.showSpinner = false;
      }

      if (routerEvent instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        if (routerEvent.url != routerEvent.urlAfterRedirects) {
          this.currentUrl = routerEvent.urlAfterRedirects;
        } else {
          this.currentUrl = routerEvent.url;
        }

        this.sharedService.setCurrentUrl(this.currentUrl);

        if (isPlatformBrowser(this.platformId)) {
          gtag('set', 'page_path', routerEvent.urlAfterRedirects);
          gtag('set', 'user_properties', {
            'Idenedi': this.authService.getCurrentUserId(),
            'IsLoggedIn': this.authService.isLoggedIn(),
          });
          gtag('event', 'page_view');
        }
      }
    });

    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd && this.currentUrl !== this.previousUrl),
      map(() => this._route),
      map((route) => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route) => {
        return route.data
      })
    )
      .subscribe((data) => {
        this.setMetaData(data.metaData);
        this.setBannerdata(data?.metaData?.banner);
        if (data?.productDetail?.fullScreen || data?.productDetail?.fullScreenConnected) {
          this.stemexeDataProvider.setPageWidth('full');
        } else {
          this.stemexeDataProvider.setPageWidth(data?.pageWidth || 'default');
        }
        this.setFooter(data?.pageWidth != 'full');

      });

    this.initializeApp();
    // this.consoleToggleService.disableConsoleInProduction();
  }
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.data?.action == 'PuppeteerNavigate' && event.data?.url) {
      this._router.navigateByUrl(event.data?.url);
    }
  }

  onLoadingStart() {
    this.document?.getElementById('pageRendered')?.remove();
  }
  onLoadingEnd() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      const span = this.document?.createElement('span');
      if (span) {
        span.id = 'pageRendered';
        this.document?.body?.appendChild(span);
      }
    }, 300);
  }

  // Priority: page level metadata (e.g. topic) > tab level metadata (tab customization) > group metadata (App Appearance) > defaults
  setMetaData(metaData?: any) {
    combineLatest([this.getTitlePostfix(), this.metaService.getGroupMetaData()]).pipe(take(1))
      .subscribe(([pf, groupMetaData]) => {
        // Page has already set its own metadata (e.g. topic details), don't override it
        if (this.metaService.hasPageMetaData()) {
          return;
        }
        // Group only title is prefixed with app name e.g. "Stemexe | Group Meta"
        const groupTitle = groupMetaData?.metaTitle ? `${pf} | ${groupMetaData.metaTitle}` : null;
        // Tab title is postfixed with app name e.g. "Tab Meta | Stemexe"
        const title = metaData?.metaTitle ? `${metaData.metaTitle} | ${pf}` : groupTitle;
        const description = metaData?.metaDescription || groupMetaData?.metaDescription;

        this.metaService.setMetaData({
          title: title || null,
          description: description || null,
          imageUrl: `${environment.webHost}/assets/images/meta-preview-img.png`
        });

        if (title) {
          this.metaService.setTitle(title);
        } else {
          this.metaService.setDefaultMetaData();
        }
      });
  }


  getTitlePostfix() {
    if (environment.build == ENV_BUILD.STEMEXE) {      //if it's stemexe build

      if (environment.enableGroupsSelection) {//set default stemxe title if group selection is enabled
        return of('Stemexe');
        //
      } else if (this.authService.idenediGroupId) {
        return this.groupsService.getUserGroupsProfiles([this.authService.idenediGroupId])
          .pipe(
            map(groupInfo => groupInfo?.FirstName || 'Stemexe'))
      } else {
        return of('Stemexe');

      }
    } else if (environment.build == ENV_BUILD.EXCEEDERS) {
      return of('Exceeders');
    } else if (environment.build == ENV_BUILD.EBUILD) {
      return of('ebuild');
    } else {
      return of('Stemexe');
    }
  }

  initializeApp() {
    this.metaService._setDefaultMetaData();
  }

  setBannerdata(data) {
    const urlFragments = this._router.url.split('/');
    const isCustomTab = this._route.snapshot.queryParams?.tabId
    if ((urlFragments.length == 3 && urlFragments[1] == 'pages') || isCustomTab) {
      this.sharedService.bannerSubject.next(data);
    } else {
      this.sharedService.bannerSubject.next(null);
    }
  }

  setFooter(show: boolean) {
    const urlFragments = this._router.url.split('/');
    const isCustomTab = this._route.snapshot.queryParams?.tabId
    if ((urlFragments.length == 3 && urlFragments[1] == 'pages') || isCustomTab) {
      this.sharedService.footerSubject$.next(show);
    } else {
      this.sharedService.footerSubject$.next(false);
    }
  }

  ngOnInit() {
  }


}
